// src/components/HotEventList.js
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";

import {
  Typography,
  Button,
  Box,
  Switch,
  FormControlLabel,
} from "@mui/material";

import WarningIcon from "@mui/icons-material/Warning";
import AppHeader from "./AppHeader";


import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const API_BASE = process.env.REACT_APP_EMERGENCY_API_URL || "http://localhost:3000/api/emergency";

const HotEventList = ({ setIsLoggedIn }) => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  // const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true); 

  // --------- FORMATTERS ----------
  const formatDateTime = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    return d.toLocaleString("en-GB", {
      timeZone: "Asia/Yangon",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusCellClass = (status) => {
    if (status === "open") return "status-open";
    if (status === "closed") return "status-closed";
    return "";
  };

  // --------- FETCH DATA ----------
  const fetchData = useCallback(
    async (pageNum = 1) => {
      try {
        // setLoading(true);
        const tenantCode =
        localStorage.getItem("tenant");
        console.log("Tenant Code",tenantCode);
        const res = await axios.get(`${API_BASE}/admin/hot/open`, {
          headers: {
            "x-tenant": tenantCode || ""
          },
          withCredentials: true,
          params: {
            page: pageNum,
            pageSize,
          },
        });

        if (res.data?.success) {
          setRows(res.data.rows || []);
          setTotal(res.data.total || 0);
          setPage(res.data.page || pageNum);
        } else {
          setRows([]);
          setTotal(0);
        }
      } catch (err) {
        console.error("Error loading hot events:", err);
      }
      // finally { setLoading(false); }
    },
    [pageSize]
  );

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  // Auto-refresh every 30s
  useEffect(() => {
    if (!autoRefresh) return;

    const id = setInterval(() => {
      fetchData(page);
    }, 30000);

    return () => clearInterval(id);
  }, [autoRefresh, fetchData, page]);

  // --------- AG GRID CONFIG ----------
  const columnDefs = [
    { headerName: "Event ID", field: "event_id", width: 110 },
    {
      headerName: "Staff",
      field: "full_name",
      flex: 1.5,
      valueGetter: (params) =>
        `${params.data.full_name || ""} (${params.data.staff_code || ""})`,
    },
    { headerName: "Dept", field: "department", flex: 1 },
    {
      headerName: "Severity",
      field: "severity",
      width: 120,
      cellRenderer: (params) => (
        <span style={{ color: "#d32f2f", fontWeight: 600 }}>
          {params.value || "critical"}
        </span>
      ),
    },
    {
      headerName: "Event Status",
      field: "event_status",
      width: 130,
      cellClass: (params) => statusCellClass(params.value),
    },
    {
      headerName: "Participant Status",
      field: "participant_status",
      width: 150,
    },
    {
      headerName: "Started At",
      field: "started_at",
      flex: 1.2,
      valueFormatter: (params) => formatDateTime(params.value),
    },
    {
      headerName: "Last Reported",
      field: "last_reported_at",
      flex: 1.2,
      valueFormatter: (params) => formatDateTime(params.value),
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 140,
      cellRenderer: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            navigate(`/emergency/hot/${params.data.event_id}`)
          }
        >
          View
        </Button>
      ),
    },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
  };

  const onRefreshClick = () => {
    fetchData(page);
  };


  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* App Bar */}
      <AppHeader onLogout={() => {
        localStorage.removeItem("token"); // Optional: Extra safety to ensure token is gone
        setIsLoggedIn(false);
      }} />

      {/* Filters + actions */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderBottom: "1px solid #eee",
        }}
      >
        <WarningIcon sx={{ color: "#d32f2f" }} />
        <Typography variant="h6">Open Hot Button Events</Typography>

        <Box sx={{ flexGrow: 1 }} />

        <FormControlLabel
          control={
            <Switch
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              color="primary"
            />
          }
          label="Auto refresh (30s)"
        />

        <Button variant="outlined" onClick={onRefreshClick}>
          Refresh
        </Button>
      </Box>

      {/* AG Grid */}
      <Box sx={{ flexGrow: 1 }}>
        <div
          className="ag-theme-alpine"
          style={{ height: "100%", width: "100%" }}
        >
          <AgGridReact
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            animateRows={true}
            overlayLoadingTemplate={"<span class='ag-overlay-loading-center'>Loading...</span>"}
            loadingOverlayComponentParams={{ loadingMessage: "Loading..." }}
          />
        </div>
      </Box>

      {/* Pagination info (simple) */}
      <Box sx={{ p: 1, textAlign: "right", fontSize: 12, color: "#666" }}>
        Total events: {total} | Page: {page}
      </Box>

      <style>{`
        .status-open {
          background-color: #ffebee;
          color: #c62828;
          font-weight: 600;
        }
        .status-closed {
          background-color: #e8f5e9;
          color: #2e7d32;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default HotEventList;